import { useCallback, useState } from 'react'
import { Trans } from 'react-i18next'
import clsx from 'clsx'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { TableAdapter, TableColumnListValue, TypographyGroupValue, TypographySelectValue } from '@makyoapp/makyo-common'
import {
  AlignBottom16,
  AlignCenter16,
  AlignLeft16,
  AlignMiddle16,
  AlignRight16,
  AlignTop16,
  ArrowShortUp16,
  DrawerAlt16,
  TextAlignCenter16,
  TextAlignJustified16,
  TextAlignLeft16,
  TextAlignRight16,
} from '@makyoapp/makyo-icon/icon'
import { ColorVariant } from '@makyoapp/makyo-tw-ui'
import { Divider, Field, NumberInput, SingleIconSelect, Switch } from '@makyoapp/makyo-ui'

import { IViewComponent } from '@/core/app-builder/app/component'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useCompSettings } from '@/hooks/__project__/useCompSettings'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import { useUpdateCompSettings, useUpdateCompSettingValue } from '@/hooks/useUpdateCompSettings'
import {
  BuilderComponent,
  BuilderComponentSetting,
  BuilderComponentSettingType,
  ComponentSettingKey,
  FlattenedItem,
  SpacingInputUnit,
} from '@/shared/builder'
import { arrayFillNumber } from '@/shared/builder-utils'
import { SpacingInputPosition, SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'
import { BrandKitFontV2 } from '@/components/BrandKit/BrandKitFontV2'
import { ColumnListRepeater } from '@/components/Builder/ColumnListRepeater'
import DataSetLimitInput from '@/components/Builder/DataSetLimitInput'
import { GridColumnSlider } from '@/components/Builder/GridColumnSlider'
import { GroupSorting } from '@/components/Builder/GroupSorting'
import { LayoutColumnSelect } from '@/components/Builder/LayoutColumnSelect'
import { RadiusInput, RadiusInputValue } from '@/components/Builder/RadiusInput'
import { ScreenDropdownV2 } from '@/components/Builder/ScreenDropdownV2'
import { ScreenRepeaterV2 } from '@/components/Builder/ScreenRepeaterV2'
import { SectionBackgroundSelect } from '@/components/Builder/Section'
import { SpaceFieldWithUnit } from '@/components/Builder/SpaceFieldWithUnit'
import { SpacingInput } from '@/components/Builder/SpacingInput'
import { TableColumnList } from '@/components/Builder/TableColumnList'
import { TableDataSourceWithColumnDropdown } from '@/components/Builder/TableDataSourceWithColumnDropdown'
import { Help } from '@/components/Help'
import { TypographySelect } from '@/components/TypographySelect/TypographySelect'

import {
  ColorVariantFieldSetting,
  ColumnFilterFieldSetting,
  ColumnSelectFieldSetting,
  DatePickerFieldSetting,
  IconFieldSetting,
  IconSelectFieldSetting,
  MenuSelectFieldSetting,
  NumberWithUnitFieldSetting,
  SelectFieldSetting,
  SliderFieldSetting,
  SliderSelectFieldSetting,
  SortBySelectFieldSetting,
  TableSelectFieldSetting,
  TabsItemsFieldSetting,
  TextFieldSetting,
  VariableSelectFieldSetting,
  VariableTextFieldSetting,
  VisibilityFieldSetting,
} from './FieldSetting'
import ColorScheme from './FieldSetting/ColorScheme'
import SegmentedSelectFieldSetting from './FieldSetting/SegmentedSelectFieldSetting'
import UserColumnFilterFieldSetting from './FieldSetting/UserColumnFilterFieldSetting'

function ComponentSettingField({
  label,
  id,
  type,
  disabledValue,
  iconSelectSettings,
  sliderSettings,
  sliderSelectSettings,
  variableSelectSettings,
  variableTextSettings,
  textSettings,
  selectSettings,
  segmentedSelectSettings,
  numberSettings,
  numberWithUnitSettings,
  gridColumnSettings,
  screenSelectSettings,
  colorVariantSettings,
  columnFilterSettings,
  spacingSettings,
  menuSelectSettings,
  sortBySelectSettings,
  columnSelectSettings,
  tableSelectWithColumnSettings,
  typographyGroupSettings,
  tableColumnListSettings,
  tableSelectSettings,
  dependOn,
  forceHorizontal = false,
  showLabel: showLabelProp = true,
  placeholder: rawPlaceholder,
  validation,
  help,
  flattenedSettings,
  flattenedComponents,
  comp,
  groupItems,
  ancestors,
  onValueChange,
  imageSelectSettings,
  videoSelectSettings,
  defaultValue,
  selectIcon,
  showDeviceVisibility,
  groupSortingSettings,
  downloadLinkSettings,
}: BuilderComponentSetting & {
  flattenedComponents: FlattenedItem[]
  flattenedSettings: BuilderComponentSetting[]
  comp: BuilderComponent
  ancestors: FlattenedItem[]
}) {
  const { dbClient, tablesMap, tables } = useDbClientContext()
  const { activeComponent, activeLayerClient, activeLayerElementSettings, activeLayerTableId } = useActiveLayerContext()
  const [activeDevice] = useActiveDevice()

  const activeComponentId = activeComponent?.id ?? ''
  const component = activeLayerClient?.componentsMap?.get(activeComponentId) as IViewComponent

  const updateValue = useUpdateCompSettingValue(id)
  const updateSettings = useUpdateCompSettings(activeComponentId)

  const settingId = `${activeComponentId}-${id}`

  const settings = useCompSettings(component)

  // Determine the current value of the component setting.
  // If the activeComponent exists and has a setting with the given id, use that value.
  // If the setting value is undefined, use the default value (undefined in this case).
  // If the activeComponent does not exist, the value is undefined.
  const value = settings?.[id]

  const ancestorFormId = ancestors?.find((a) => a.compId === 'form')?.id
  const ancestorFormSettings = ancestorFormId ? (activeLayerElementSettings?.[ancestorFormId] ?? {}) : {}
  const ancestorFormDataSource = ancestorFormSettings[ComponentSettingKey.FormDataSource]

  const formContext = {
    dataSource: ancestorFormDataSource,
    dbTable: ancestorFormDataSource ? tablesMap.get(ancestorFormDataSource) : undefined,
    settings: ancestorFormSettings,
  }

  // Determine whether this setting should be shown.
  // If there is a dependency and an active component, use the showWhen function from the dependency to determine visibility.
  // If there is no dependency or no active component, the setting should be shown by default (set to true).
  const shouldShow =
    dependOn && activeComponent
      ? dependOn.showWhen(settings[dependOn.id], activeComponent.id, ancestors, undefined, formContext)
      : true

  const isArr = Array.isArray(value)

  const [error, setError] = useState('')

  // Function to find related column settings for a given table.
  // It filters the flattenedSettings for settings of type ColumnSelect and which are not marked as noAutoSelect and have the same tableSourceId as the current setting.
  // For each filtered setting, it finds a column in the table that matches the allowed columnTypes, defaulting to Text if no columnTypes are specified.
  // It returns an array of objects, each containing the id of the related setting and the id of the found column.
  const findRelatedColSettings = useCallback(
    (table: TableAdapter) => {
      const usedColumns = new Set<string>()

      return flattenedSettings
        .filter(
          (s) =>
            s.type === BuilderComponentSettingType.ColumnSelect &&
            s.columnSelectSettings?.noAutoSelect !== true &&
            s.columnSelectSettings?.tableSourceId === id
        )
        .map((rs) => {
          // Special case 1: CardDesignMediaIconColorColumn - Only find color columns
          if (rs.id === ComponentSettingKey.CardDesignMediaIconColorColumn) {
            const colorColumn = table.columns.find((c) => c.type === ColumnTypes.Color)
            if (colorColumn && !usedColumns.has(colorColumn.id)) {
              usedColumns.add(colorColumn.id)
              return {
                id: rs.id,
                value: colorColumn.id,
              }
            }
            return {
              id: rs.id,
              value: '', // Return empty string if no color column found
            }
          }

          // Special case 2: thumbnail/media fields - Find Image/Icon columns
          if (['thumbnail', 'media'].some((r) => rs.id.includes(r))) {
            const imageColumn = table.columns.find(
              (c) => [ColumnTypes.Image, ColumnTypes.Icon].includes(c.type) && !usedColumns.has(c.id)
            )
            if (imageColumn) {
              usedColumns.add(imageColumn.id)
              return {
                id: rs.id,
                value: imageColumn.id,
              }
            }
            return {
              id: rs.id,
              value: undefined,
            }
          }

          // Special case 3: CardDataSubHeader, CardDataTitle - Return empty
          if ([ComponentSettingKey.CardDataSubHeader, ComponentSettingKey.CardDataTitle].includes(rs.id as any)) {
            return {
              id: rs.id,
              value: '',
            }
          }

          // Default case: Find unused column based on columnTypes
          const availableColumn = table.columns.find((c) => {
            const isTypeMatch = (rs.columnSelectSettings?.columnTypes || [ColumnTypes.Text]).includes(c.type)
            const isNotUsed = !usedColumns.has(c.id)
            return isTypeMatch && isNotUsed
          })

          if (availableColumn) {
            usedColumns.add(availableColumn.id) // Mark as used
          }

          return {
            id: rs.id,
            value: availableColumn?.id,
          }
        })
      // NOTE: old version still here, will be removed later
      // .map((rs) => {
      //   if (rs.id === ComponentSettingKey.CardDesignMediaIconColorColumn) {
      //     // Only find color columns
      //     const colorColumn = table.columns.find((c) => c.type === ColumnTypes.Color)
      //     return {
      //       id: rs.id,
      //       value: colorColumn?.id || '', // Return empty string if no color column found
      //     }
      //   }

      //   if (['thumbnail', 'media'].some((r) => rs.id.includes(r))) {
      //     let defaultCol = table.columns.find((c) => [ColumnTypes.Image, ColumnTypes.Icon].includes(c.type))
      //     return {
      //       id: rs.id,
      //       value: defaultCol?.id,
      //     }
      //   }
      //   if ([ComponentSettingKey.CardDataSubHeader, ComponentSettingKey.CardDataTitle].includes(rs.id as any)) {
      //     return {
      //       id: rs.id,
      //       value: '',
      //     }
      //   }
      //   return {
      //     id: rs.id,
      //     value: table.columns.find((c) =>
      //       (rs.columnSelectSettings?.columnTypes || [ColumnTypes.Text]).includes(c.type)
      //     )?.id,
      //   }
      // })
    },
    [id, flattenedSettings]
  )

  // Function to find related column settings for a given table.
  // It filters the flattenedSettings for settings of type ColumnSelect and which are not marked as noAutoSelect and have the same tableSourceId as the current setting.
  // For each filtered setting, it finds a column in the table that matches the allowed columnTypes, defaulting to Text if no columnTypes are specified.
  // It returns an array of objects, each containing the id of the related setting and the id of the found column.
  const findRelatedFilterSettings = useCallback(() => {
    return flattenedSettings
      .filter(
        (s) => s.type === BuilderComponentSettingType.ColumnFilter && s.columnFilterSettings?.tableSourceId === id
      )
      .map((rs) => {
        return {
          id: rs.id,
          value: {
            isAny: false,
            conditions: [],
          },
        }
      })
  }, [id, flattenedSettings])

  // Function to find user filter settings.
  // It filters the flattenedSettings for settings of type UserColumnFilter which have the same tableSourceId as the current setting.
  // For each filtered setting, it creates an empty array as the filter value.
  // It returns an array of objects, each containing the id of the related setting and the empty array.
  const findUserFilterSettings = useCallback(() => {
    return flattenedSettings
      .filter(
        (s) => s.type === BuilderComponentSettingType.UserColumnFilter && s.columnFilterSettings?.tableSourceId === id
      )
      .map((rs) => {
        return {
          id: rs.id,
          value: [],
        }
      })
  }, [id, flattenedSettings])

  const handleUpdateValue = useCallback(
    (v: any) => {
      updateValue(v)

      if (!dbClient) return
      if (onValueChange) {
        updateSettings((prevSettings) => {
          return onValueChange(v, prevSettings, dbClient)
        })
      }
    },
    [dbClient, onValueChange, updateSettings, updateValue]
  )

  let field = null,
    wrapField = true,
    showLabel = showLabelProp,
    isVertical = false

  let disabled = false
  if (typeof shouldShow === 'boolean' && !shouldShow) {
    return null
  }

  if (shouldShow === 'disabled') {
    disabled = true
  }

  if (type === BuilderComponentSettingType.Divider) {
    return (
      <div>
        <Divider />
      </div>
    )
  }

  if (type === BuilderComponentSettingType.DownloadLink) {
    const { text, link, openInNewTab = true } = downloadLinkSettings ?? {}

    if (!text || !link) {
      return null
    }

    return (
      <div className='py-2'>
        <a
          href={link}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          className='text-primary-600 hover:text-primary-700 underline flex items-center gap-2'
          download
        >
          {text}
        </a>
      </div>
    )
  }

  if (!activeComponent) {
    return null
  }

  switch (type) {
    // ok ✅
    case BuilderComponentSettingType.Text: {
      field = (
        <TextFieldSetting
          id={id}
          settingId={settingId}
          componentId={activeComponentId}
          value={value}
          textSettings={textSettings}
        />
      )
      isVertical = true
      break
    }

    // ok ✅
    case BuilderComponentSettingType.Number: {
      const { min = 0, max = 10, step = 1 } = numberSettings ?? {}

      field = (
        <NumberInput
          id={settingId}
          min={min}
          max={max}
          step={step}
          value={value as number}
          onChange={async (nextVal) => {
            updateValue(nextVal)
          }}
        />
      )
      break
    }

    // ok ✅
    case BuilderComponentSettingType.NumberWithUnit: {
      field = (
        <NumberWithUnitFieldSetting
          id={id}
          settingId={settingId}
          value={value}
          numberWithUnitSettings={numberWithUnitSettings}
        />
      )

      break
    }

    // ok ✅
    case BuilderComponentSettingType.Switch: {
      field = (
        <Switch
          id={settingId}
          checked={disabled ? (disabledValue as boolean) : ((value as boolean) ?? false)}
          onClick={handleUpdateValue}
          disabled={disabled}
        />
      )
      break
    }
    // ok ✅
    case BuilderComponentSettingType.Select: {
      field = (
        <SelectFieldSetting
          selectSettings={selectSettings}
          ancestors={ancestors}
          settingId={settingId}
          id={id}
          value={value}
        />
      )
      isVertical = true
      break
    }

    // ok ✅
    case BuilderComponentSettingType.SegmentedSelect: {
      field = (
        <SegmentedSelectFieldSetting
          segmentedSelectSettings={segmentedSelectSettings}
          ancestors={ancestors}
          settingId={settingId}
          id={id}
          value={value}
        />
      )
      isVertical = true
      break
    }

    // ok ✅
    case BuilderComponentSettingType.IconSelect: {
      field = (
        <IconSelectFieldSetting
          id={id}
          settingId={settingId}
          ancestors={ancestors}
          iconSelectSettings={iconSelectSettings}
          value={value}
        />
      )
      break
    }

    // ok ✅
    case BuilderComponentSettingType.Slider: {
      field = (
        <SliderFieldSetting
          id={id}
          settingId={settingId}
          value={value}
          sliderSettings={sliderSettings}
          onChange={updateValue}
        />
      )
      isVertical = true
      break
    }

    // ok ✅
    case BuilderComponentSettingType.SliderSelect: {
      field = (
        <SliderSelectFieldSetting
          id={id}
          settingId={settingId}
          value={value}
          sliderSelectSettings={sliderSelectSettings}
        />
      )
      isVertical = true
      break
    }

    // ok ✅
    case BuilderComponentSettingType.DatePicker: {
      field = <DatePickerFieldSetting id={id} value={value} />
      break
    }

    // todo
    case BuilderComponentSettingType.TimePicker: {
      break
    }

    // ok ✅
    case BuilderComponentSettingType.VariableSelect: {
      field = (
        <VariableSelectFieldSetting
          id={id}
          settingId={settingId}
          variableSelectSettings={variableSelectSettings}
          value={value}
          formContext={formContext}
          settings={settings}
        />
      )
      isVertical = true
      break
    }

    // ok ✅
    case BuilderComponentSettingType.VariableText: {
      field = (
        <VariableTextFieldSetting
          id={id}
          value={value}
          validation={validation}
          rawPlaceholder={rawPlaceholder}
          variableTextSettings={variableTextSettings}
          settingId={settingId}
        />
      )
      isVertical = true
      break
    }

    // ok ✅
    case BuilderComponentSettingType.ColorScheme:
      field = <ColorScheme settingId={settingId} value={value} onChange={updateValue} id={id} />
      isVertical = true
      break

    // ok ✅
    case BuilderComponentSettingType.GridColumns: {
      const { numColumnId } = gridColumnSettings ?? {}
      const columnCount =
        activeComponent && numColumnId ? (activeLayerElementSettings?.[activeComponent.id]?.[numColumnId] as number) : 1

      field = !columnCount ? null : (
        <div className='flex flex-col w-full'>
          <Field isVertical>
            <label>Column Width</label>
            <GridColumnSlider
              id={settingId}
              count={columnCount}
              value={(value as number[]) || arrayFillNumber(columnCount)}
              onChange={updateValue}
            />
          </Field>
        </div>
      )
      wrapField = false
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.ScreenSelect: {
      const { multiple = false, repeater = false, isLoginComponent = false } = screenSelectSettings ?? {}

      if (!repeater) {
        field = (
          <ScreenDropdownV2
            id={settingId}
            multiple={multiple}
            value={value as string}
            onChange={updateValue}
            isLoginComponent={isLoginComponent}
          />
        )
      } else {
        field = <ScreenRepeaterV2 label={label} values={isArr ? (value as string[]) : []} onChange={updateValue} />
        wrapField = false
        showLabel = false
      }
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.TableSelect: {
      const { detailScreenDependId } = tableSelectSettings || {}
      const shouldShow = activeLayerTableId
        ? detailScreenDependId
          ? !Boolean(activeLayerElementSettings?.[activeComponent.id || '']?.[detailScreenDependId])
          : true
        : true

      wrapField = shouldShow
      field = (
        <TableSelectFieldSetting
          id={id}
          componentId={activeComponentId}
          settingId={settingId}
          value={value}
          findRelatedColSettings={findRelatedColSettings}
          tableSelectSettings={tableSelectSettings}
          findRelatedFilterSettings={findRelatedFilterSettings}
          findUserFilterSettings={findUserFilterSettings}
        />
      )
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.TableSelectWithColumns: {
      const columnTypes = tableSelectWithColumnSettings?.columnTypes

      field = (
        <TableDataSourceWithColumnDropdown
          tables={tables}
          columnTypes={columnTypes}
          id={settingId}
          value={value as { table: string; columns: string[] }}
          onChange={updateValue}
        />
      )
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.SortBySelect: {
      field = (
        <SortBySelectFieldSetting
          id={id}
          settingId={settingId}
          value={value}
          sortBySelectSettings={sortBySelectSettings}
        />
      )
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.ColumnSelect: {
      field = (
        <ColumnSelectFieldSetting
          id={id}
          settingId={settingId}
          componentId={activeComponentId}
          value={value}
          flattenedComponents={flattenedComponents}
          comp={comp}
          ancestors={ancestors}
          columnSelectSettings={columnSelectSettings}
        />
      )
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.Spacing: {
      field = (
        <SpacingInput
          id={settingId}
          values={value as SpacingInputValue}
          onChange={updateValue}
          max={spacingSettings?.max}
        />
      )
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.SpacingSingle: {
      field = (
        <SpaceFieldWithUnit
          id={id}
          icon={<ArrowShortUp16 />}
          value={
            (value as {
              value: number
              unit: SpacingInputUnit
            }) ?? {
              value: 0,
              unit: 'px',
            }
          }
          max={spacingSettings?.max}
          onChange={async (value, unit) => {
            await updateValue({ value, unit })
          }}
        />
      )
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.Radius: {
      field = <RadiusInput id={settingId} values={value as RadiusInputValue} onChange={updateValue} />
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.Icon: {
      field = <IconFieldSetting id={id} settingId={settingId} value={value} />
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.ColorVariant: {
      isVertical = true
      field = (
        <ColorVariantFieldSetting
          id={id}
          settingId={settingId}
          value={value}
          defaultValue={defaultValue as ColorVariant}
          compSettings={settings}
          colorVariantSettings={colorVariantSettings}
        />
      )
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.TextAlignment: {
      field = (
        <SingleIconSelect
          id={settingId}
          onClick={async (opt) => {
            await updateValue(opt.id)
          }}
          options={[
            {
              icon: <TextAlignLeft16 />,
              id: 'start',
              title: 'Start',
            },
            {
              icon: <TextAlignCenter16 />,
              id: 'center',
              title: 'Center',
            },
            {
              icon: <TextAlignRight16 />,
              id: 'end',
              title: 'End',
            },
            {
              icon: <TextAlignJustified16 />,
              id: 'justify',
              title: 'Justify',
            },
          ]}
          selected={['start', 'center', 'end', 'justify'].includes(value as string) ? (value as string) : 'start'}
        />
      )
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.ComponentAlignment: {
      field = (
        <SingleIconSelect
          id={settingId}
          onClick={async (opt) => {
            await updateValue(opt.id)
          }}
          options={[
            {
              icon: <AlignLeft16 />,
              id: 'start',
              title: 'Start',
            },
            {
              icon: <AlignCenter16 />,
              id: 'center',
              title: 'Center',
            },
            {
              icon: <AlignRight16 />,
              id: 'end',
              title: 'End',
            },
            {
              icon: <DrawerAlt16 />,
              id: 'parent',
              title: 'Parent',
            },
          ]}
          selected={['start', 'center', 'end', 'parent'].includes(value as string) ? (value as string) : 'center'}
        />
      )
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.HorizontalAlignment: {
      field = (
        <SingleIconSelect
          id={settingId}
          onClick={async (opt) => {
            await updateValue(opt.id)
          }}
          options={[
            {
              icon: <AlignLeft16 />,
              id: 'start',
              title: 'Start',
            },
            {
              icon: <AlignCenter16 />,
              id: 'center',
              title: 'Center',
            },
            {
              icon: <AlignRight16 />,
              id: 'end',
              title: 'End',
            },
          ]}
          selected={['start', 'center', 'end'].includes(value as string) ? (value as string) : 'center'}
        />
      )
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.VerticalAlignment: {
      field = (
        <SingleIconSelect
          id={settingId}
          onClick={async (opt) => {
            await updateValue(opt.id)
          }}
          options={[
            {
              icon: <AlignTop16 />,
              id: 'start',
              title: 'Top',
            },
            {
              icon: <AlignMiddle16 />,
              id: 'center',
              title: 'Middle',
            },
            {
              icon: <AlignBottom16 />,
              id: 'end',
              title: 'Bottom',
            },
          ]}
          selected={['start', 'center', 'end'].includes(value as string) ? (value as string) : 'center'}
        />
      )
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.Group: {
      const entries = groupItems ? Object.entries(groupItems) : []

      let acc = null
      if (entries.length) {
        acc = entries.map(([k, v]) => {
          return (
            <AccordionItem key={`${settingId}-${k}`} itemId={k} title={k} items={v}>
              {v.map((s, si) => (
                <ComponentSettingField
                  comp={comp}
                  {...s}
                  key={`data-${s.id}${si}`}
                  ancestors={ancestors}
                  flattenedSettings={flattenedSettings}
                  flattenedComponents={flattenedComponents}
                />
              ))}
            </AccordionItem>
          )
        })
      }

      field = (
        <div className='border-x border-t bdr bg-zinc-50 dark:bg-slate-700 rounded-md'>
          <Accordion openMultiple={false} initialActiveItems={[]}>
            {acc}
          </Accordion>
        </div>
      )
      showLabel = false
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.TypographySelect: {
      field = <TypographySelect onChange={updateValue} id={settingId} value={value as TypographySelectValue} />
      showLabel = false
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.TypographyGroup: {
      const { items = [] } = typographyGroupSettings ?? {}

      const acc = items.map(({ id, label }, i) => {
        return (
          <AccordionItem
            key={`${settingId}-${id}`}
            itemId={id}
            title={label}
            className={clsx(i === items.length - 1 && 'rounded-b-md')}
          >
            <TypographySelect
              onChange={async (nextValue) => {
                await updateValue({
                  ...((value || {}) as TypographyGroupValue),
                  [id]: nextValue,
                })
              }}
              id={`${settingId}-${id}`}
              value={(value as TypographyGroupValue)?.[id]}
            />
          </AccordionItem>
        )
      })
      field = (
        <div className='border-x border-t bdr bg-zinc-50 dark:bg-slate-800 rounded-md'>
          <Accordion openMultiple={false} initialActiveItems={[items[0]?.id]}>
            {acc}
          </Accordion>
        </div>
      )
      showLabel = false
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.ColumnFilter: {
      field = (
        <ColumnFilterFieldSetting
          id={id}
          settingId={settingId}
          value={value}
          columnFilterSettings={columnFilterSettings}
        />
      )
      showLabel = false
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.ColumnList: {
      field = (
        <div className='flex flex-col gap-4'>
          <ColumnListRepeater onChange={updateValue} value={isArr ? (value as never) : []} id={settingId} />
        </div>
      )
      showLabel = false
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.TableColumnList: {
      const { showModal } = tableColumnListSettings ?? {}

      field = (
        <div className='flex flex-col gap-4'>
          <TableColumnList
            id={settingId}
            value={(typeof value !== 'object' ? undefined : value) as TableColumnListValue}
            onChange={updateValue}
            tables={tables}
            tableColumnListSettings={tableColumnListSettings}
            type='single-row'
          />
        </div>
      )
      showLabel = false
      isVertical = true
      break
    }

    case BuilderComponentSettingType.DataTableColumnList: {
      const showModal = (tableColumnListSettings as any)?.showModal ?? false

      field = (
        <div className='flex flex-col gap-4'>
          <TableColumnList
            id={settingId}
            value={(typeof value !== 'object' ? undefined : value) as TableColumnListValue}
            onChange={updateValue}
            tables={tables}
            noCustom
            excludeColumnTypes={[ColumnTypes.Slug]}
            activeDevice={activeDevice}
            tableColumnListSettings={tableColumnListSettings}
            selectIcon={selectIcon}
            showDeviceVisibility={showDeviceVisibility}
            showModal={showModal}
            type='data-table'
          />
        </div>
      )
      showLabel = false
      isVertical = true
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.UserColumnFilter: {
      field = (
        <UserColumnFilterFieldSetting
          id={id}
          settingId={settingId}
          value={value}
          columnFilterSettings={columnFilterSettings}
        />
      )
      isVertical = false
      showLabel = false
      break
    }

    // ✅ ok
    case BuilderComponentSettingType.MenuSelect: {
      field = (
        <MenuSelectFieldSetting id={id} settingId={settingId} value={value} menuSelectSettings={menuSelectSettings} />
      )
      isVertical = true
      break
    }

    case BuilderComponentSettingType.SectionBackgroundGroup: {
      field = (
        <SectionBackgroundSelect
          id={id}
          value={value}
          settingId={settingId}
          compSettings={settings}
          colorVariantSettings={colorVariantSettings}
          imageSelectSettings={imageSelectSettings}
          videoSelectSettings={videoSelectSettings}
        />
      )
      showLabel = false
      break
    }

    case BuilderComponentSettingType.GroupSorting: {
      field = <GroupSorting id={id} value={value} groupSortingSettings={groupSortingSettings} />
      break
    }

    case BuilderComponentSettingType.LayoutColumnSelect: {
      field = <LayoutColumnSelect id={id} value={value} onChange={updateValue} />
      break
    }

    case BuilderComponentSettingType.DataSetLimit: {
      field = <DataSetLimitInput id={id} settingId={settingId} value={value} label={label} />
      forceHorizontal = true
      showLabel = false
      break
    }

    // New fielt settings for tabs
    case BuilderComponentSettingType.TabsItems: {
      field = <TabsItemsFieldSetting id={id} componentId={activeComponentId} />
      isVertical = true
      break
    }

    case BuilderComponentSettingType.Visibility: {
      field = <VisibilityFieldSetting id={id} value={value} />
      isVertical = true
      break
    }
  }

  const errorEl = value && error ? <div className='text-m-sm text-light red'>{error}</div> : null

  return wrapField ? (
    <Field defaultSize={false} noPad isVertical={forceHorizontal ? false : isVertical} className='justify-between py-3'>
      {showLabel && label && (
        <label className={clsx('flex-c', forceHorizontal && 'min-w-[40%] w-[40%]')} htmlFor={settingId}>
          <div>
            <Trans>{label}</Trans>
          </div>
          {help && <Help>{help}</Help>}
        </label>
      )}
      {!showLabel || (!label && help && <div className='text-m-sm text-light'>{help}</div>)}
      {field}
      {errorEl}
    </Field>
  ) : (
    <>
      {field}
      {errorEl}
    </>
  )
}

export default ComponentSettingField
